import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import { 
  FaUserTie, 
  FaGraduationCap, 
  FaGavel, 
  FaVideo, 
  FaSave, 
  FaCheckCircle, 
  FaPlus, 
  FaTimes, 
  FaInfoCircle,
  FaBriefcase,
  FaBuilding,
  FaLink
} from "react-icons/fa";
import "../styles/SolicitorSettings.css";

export default function SolicitorSettings() {
  const { user, token } = useAuth();

  /* ======================
     STATE
  ====================== */
  const [about, setAbout] = useState("");

  // Courts
  const [courtInput, setCourtInput] = useState("");
  const [courts, setCourts] = useState([]);

  // Specializations
  const [specializationInput, setSpecializationInput] = useState("");
  const [specializations, setSpecializations] = useState([]);

  // Video URLs (MULTIPLE)
  const [videoInput, setVideoInput] = useState("");
  const [legalVideoUrls, setLegalVideoUrls] = useState([]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  /* ======================
     COURT HANDLERS
  ====================== */
  const addCourt = () => {
    if (!courtInput.trim()) {
      setMessage({ type: "error", text: "Please enter a court name" });
      return;
    }
    if (!courts.includes(courtInput.trim())) {
      setCourts([...courts, courtInput.trim()]);
      setCourtInput("");
      setMessage({ type: "success", text: "Court added" });
      setTimeout(() => setMessage({ type: "", text: "" }), 2000);
    } else {
      setMessage({ type: "error", text: "Court already exists" });
    }
  };

  const removeCourt = (court) => {
    setCourts(courts.filter((c) => c !== court));
  };

  /* ======================
     SPECIALIZATION HANDLERS
  ====================== */
  const addSpecialization = () => {
    if (!specializationInput.trim()) {
      setMessage({ type: "error", text: "Please enter a specialization" });
      return;
    }
    if (!specializations.includes(specializationInput.trim())) {
      setSpecializations([...specializations, specializationInput.trim()]);
      setSpecializationInput("");
      setMessage({ type: "success", text: "Specialization added" });
      setTimeout(() => setMessage({ type: "", text: "" }), 2000);
    } else {
      setMessage({ type: "error", text: "Specialization already exists" });
    }
  };

  const removeSpecialization = (item) => {
    setSpecializations(specializations.filter((s) => s !== item));
  };

  /* ======================
     VIDEO URL HANDLERS
  ====================== */
  const addVideoUrl = () => {
    if (!videoInput.trim()) {
      setMessage({ type: "error", text: "Please enter a video URL" });
      return;
    }
    
    // Basic URL validation
    try {
      new URL(videoInput.trim());
    } catch {
      setMessage({ type: "error", text: "Please enter a valid URL" });
      return;
    }

    if (!legalVideoUrls.includes(videoInput.trim())) {
      setLegalVideoUrls([...legalVideoUrls, videoInput.trim()]);
      setVideoInput("");
      setMessage({ type: "success", text: "Video URL added" });
      setTimeout(() => setMessage({ type: "", text: "" }), 2000);
    } else {
      setMessage({ type: "error", text: "Video URL already exists" });
    }
  };

  const removeVideoUrl = (url) => {
    setLegalVideoUrls(legalVideoUrls.filter((v) => v !== url));
  };

  /* ======================
     FETCH SETTINGS
  ====================== */
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/solicitor/settings`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data?.solicitor) {
          const s = res.data.solicitor;
          setAbout(s.about || "");
          setCourts(s.courts || []);
          setSpecializations(s.specializations || []);
          setLegalVideoUrls(s.legalVideoUrls || []);
        }
      } catch {
        console.error("Failed to fetch solicitor settings");
      }
    };

    fetchSettings();
  }, [token]);

  /* ======================
     SAVE SETTINGS
  ====================== */
  const saveSettings = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });
    
    try {
      await axios.put(
        `${API_BASE_URL}/api/solicitor/settings`,
        {
          about,
          courts,
          specializations,
          legalVideoUrls
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: "success", text: "Settings saved successfully" });
      
      // Clear success message after 3 seconds
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch {
      setMessage({ type: "error", text: "Failed to save settings" });
    } finally {
      setLoading(false);
    }
  };

  if (user.role !== "SOLICITOR") return <div className="solicitor-settings-access-denied">Access Denied</div>;

  return (
    <div className="solicitor-settings-container">
      {/* Header */}
      <div className="solicitor-settings-header">
        <div className="solicitor-settings-header-content">
          <div>
            <h1 className="solicitor-settings-title">Professional Settings</h1>
            <p className="solicitor-settings-subtitle">Manage your professional details, expertise, and resources</p>
          </div>
        </div>
      </div>

      {/* Message Alert */}
      {message.text && (
        <div className={`solicitor-settings-message solicitor-settings-message-${message.type}`}>
          <div className="solicitor-settings-message-content">
            {message.type === "success" ? <FaCheckCircle size={18} /> : <FaTimes size={18} />}
            <span>{message.text}</span>
          </div>
          <button
            className="solicitor-settings-message-close"
            onClick={() => setMessage({ type: "", text: "" })}
          >
            ×
          </button>
        </div>
      )}

      <div className="solicitor-settings-content">
        {/* About Section */}
        <div className="solicitor-settings-card">
          <div className="solicitor-settings-card-header">
            <div className="solicitor-settings-card-icon">
              <FaInfoCircle size={20} />
            </div>
            <div>
              <h3 className="solicitor-settings-card-title">Professional Profile</h3>
              <p className="solicitor-settings-card-subtitle">Tell clients about your background and expertise</p>
            </div>
          </div>
          
          <div className="solicitor-settings-form-group">
            <label className="solicitor-settings-label">
              <FaUserTie size={14} />
              <span>About You</span>
            </label>
            <textarea
              className="solicitor-settings-textarea"
              placeholder="Write about your professional background, experience, and approach to legal practice..."
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              rows={6}
            />
            <div className="solicitor-settings-char-count">
              {about.length} / 2000 characters
            </div>
          </div>
        </div>

        {/* Specializations Section */}
        <div className="solicitor-settings-card">
          <div className="solicitor-settings-card-header">
            <div className="solicitor-settings-card-icon">
              <FaGraduationCap size={20} />
            </div>
            <div>
              <h3 className="solicitor-settings-card-title">Specializations</h3>
              <p className="solicitor-settings-card-subtitle">Add your areas of legal specialization</p>
            </div>
          </div>
          
          <div className="solicitor-settings-form-group">
            <label className="solicitor-settings-label">
              <FaBriefcase size={14} />
              <span>Add Specialization</span>
            </label>
            <div className="solicitor-settings-input-group">
              <input
                type="text"
                className="solicitor-settings-input"
                placeholder="Type specialization (e.g. Property Dispute, Corporate Law, Immigration)"
                value={specializationInput}
                onChange={(e) => setSpecializationInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addSpecialization()}
              />
              <button 
                className="solicitor-settings-add-btn"
                onClick={addSpecialization}
                disabled={!specializationInput.trim()}
              >
                <FaPlus size={14} />
                <span>Add</span>
              </button>
            </div>
            
            {specializations.length > 0 && (
              <div className="solicitor-settings-tags-container">
                <div className="solicitor-settings-tags-header">
                  <span className="solicitor-settings-tags-count">{specializations.length} specialization(s)</span>
                </div>
                <div className="solicitor-settings-tags-grid">
                  {specializations.map((item, i) => (
                    <div key={i} className="solicitor-settings-tag">
                      <span className="solicitor-settings-tag-text">{item}</span>
                      <button 
                        className="solicitor-settings-tag-remove"
                        onClick={() => removeSpecialization(item)}
                        aria-label="Remove specialization"
                      >
                        <FaTimes size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Courts Section */}
        <div className="solicitor-settings-card">
          <div className="solicitor-settings-card-header">
            <div className="solicitor-settings-card-icon">
              <FaGavel size={20} />
            </div>
            <div>
              <h3 className="solicitor-settings-card-title">Courts Practiced</h3>
              <p className="solicitor-settings-card-subtitle">Add courts where you regularly practice</p>
            </div>
          </div>
          
          <div className="solicitor-settings-form-group">
            <label className="solicitor-settings-label">
              <FaBuilding size={14} />
              <span>Add Court</span>
            </label>
            <div className="solicitor-settings-input-group">
              <input
                type="text"
                className="solicitor-settings-input"
                placeholder="Type court name (e.g. High Court, District Court, Supreme Court)"
                value={courtInput}
                onChange={(e) => setCourtInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCourt()}
              />
              <button 
                className="solicitor-settings-add-btn"
                onClick={addCourt}
                disabled={!courtInput.trim()}
              >
                <FaPlus size={14} />
                <span>Add</span>
              </button>
            </div>
            
            {courts.length > 0 && (
              <div className="solicitor-settings-tags-container">
                <div className="solicitor-settings-tags-header">
                  <span className="solicitor-settings-tags-count">{courts.length} court(s)</span>
                </div>
                <div className="solicitor-settings-tags-grid">
                  {courts.map((court, i) => (
                    <div key={i} className="solicitor-settings-tag">
                      <span className="solicitor-settings-tag-text">{court}</span>
                      <button 
                        className="solicitor-settings-tag-remove"
                        onClick={() => removeCourt(court)}
                        aria-label="Remove court"
                      >
                        <FaTimes size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Video URLs Section */}
        <div className="solicitor-settings-card">
          <div className="solicitor-settings-card-header">
            <div className="solicitor-settings-card-icon">
              <FaVideo size={20} />
            </div>
            <div>
              <h3 className="solicitor-settings-card-title">Legal Video Resources</h3>
              <p className="solicitor-settings-card-subtitle">Share YouTube or Vimeo URLs of your legal advice videos</p>
            </div>
          </div>
          
          <div className="solicitor-settings-form-group">
            <label className="solicitor-settings-label">
              <FaLink size={14} />
              <span>Add Video URL</span>
            </label>
            <div className="solicitor-settings-input-group">
              <input
                type="url"
                className="solicitor-settings-input"
                placeholder="Paste YouTube or Vimeo URL (https://...)"
                value={videoInput}
                onChange={(e) => setVideoInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addVideoUrl()}
              />
              <button 
                className="solicitor-settings-add-btn"
                onClick={addVideoUrl}
                disabled={!videoInput.trim()}
              >
                <FaPlus size={14} />
                <span>Add</span>
              </button>
            </div>
            
            {legalVideoUrls.length > 0 && (
              <div className="solicitor-settings-tags-container">
                <div className="solicitor-settings-tags-header">
                  <span className="solicitor-settings-tags-count">{legalVideoUrls.length} video URL(s)</span>
                </div>
                <div className="solicitor-settings-tags-grid solicitor-settings-video-tags">
                  {legalVideoUrls.map((url, i) => (
                    <div key={i} className="solicitor-settings-tag solicitor-settings-video-tag">
                      <span className="solicitor-settings-tag-text solicitor-settings-video-url">
                        {url.length > 40 ? url.substring(0, 40) + '...' : url}
                      </span>
                      <button 
                        className="solicitor-settings-tag-remove"
                        onClick={() => removeVideoUrl(url)}
                        aria-label="Remove video URL"
                      >
                        <FaTimes size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="solicitor-settings-actions">
          <button
            className="solicitor-settings-save-btn"
            onClick={saveSettings}
            disabled={loading}
          >
            <FaSave className="solicitor-settings-btn-icon" />
            <span>{loading ? "Saving..." : "Save All Settings"}</span>
            {loading && <div className="solicitor-settings-spinner"></div>}
          </button>
          
          <div className="solicitor-settings-stats">
            <div className="solicitor-settings-stat">
              <span className="solicitor-settings-stat-number">{courts.length}</span>
              <span className="solicitor-settings-stat-label">Courts</span>
            </div>
            <div className="solicitor-settings-stat">
              <span className="solicitor-settings-stat-number">{specializations.length}</span>
              <span className="solicitor-settings-stat-label">Specializations</span>
            </div>
            <div className="solicitor-settings-stat">
              <span className="solicitor-settings-stat-number">{legalVideoUrls.length}</span>
              <span className="solicitor-settings-stat-label">Videos</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}